/**
 * Outreach — the daily ritual for one operator working through pre-written
 * LinkedIn touches by hand. Tacit never sends a message itself.
 *
 * This is not a browsable table: it holds exactly one person in focus at a
 * time, at whichever of the three connection-ladder rungs the operator has
 * selected — "what are the few things I should do right now", not "let me
 * browse a table". `sent` and `skipped` are terminal and have no ritual
 * moment of their own, so they are not reachable from this screen; the
 * per-rung header counts (needs_connection/connection_sent/connected) still
 * come from `useOutreachCounts`, which counts all five states.
 *
 * The brand selector never defaults to "all": sending a message under the
 * wrong brand's voice is the exact error this screen exists to prevent, so a
 * concrete brand is always the active filter, never an aggregate view.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Inbox, Info, Send, XCircle } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { NativeSelect } from '@/components/ui/native-select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useKeyboardShortcuts, type ShortcutConfig } from '@/hooks/useKeyboardShortcuts';
import { OutreachCard, type OutreachCardHandle } from '@/components/crm/OutreachCard';
import {
  useOutreachCounts,
  useOutreachNotesSpent,
  useOutreachQueue,
} from '@/hooks/crm/useOutreach';
import {
  OUTREACH_BRAND_LABELS,
  OUTREACH_BRANDS,
  type OutreachBrand,
  type OutreachState,
} from '@/lib/crm/outreach';
import { cn } from '@/lib/utils';

const RITUAL_STATES = ['needs_connection', 'connection_sent', 'connected'] as const;
type RitualState = (typeof RITUAL_STATES)[number];

const RUNG_FILTER_LABEL: Record<RitualState, string> = {
  needs_connection: 'to request',
  connection_sent: 'waiting',
  connected: 'ready to message',
};

const RUNG_EMPTY_TRUTH: Record<RitualState, string> = {
  needs_connection: 'Nothing needs a connection request.',
  connection_sent: 'Nothing is waiting on an answer.',
  connected: 'Nothing is ready to message.',
};

const RUNG_SHORTCUT_HINT: Record<RitualState, string> = {
  needs_connection: 'j/k to move · enter to send the bare request · o to open the profile · s to skip',
  connection_sent: 'j/k to move · enter to mark accepted · s to skip',
  connected: 'j/k to move · enter to record as sent',
};

/**
 * "This rung is empty" is never the whole truth by itself — say what's
 * happening in the other two rungs too, so "you're done here" always comes
 * with "here's what's next".
 */
function emptyRungMessage(current: RitualState, counts: Partial<Record<OutreachState, number>>): string {
  const pointers: string[] = [];

  if (current !== 'needs_connection') {
    const n = counts.needs_connection ?? 0;
    if (n > 0) pointers.push(`${n} ${n === 1 ? 'needs' : 'need'} a connection request.`);
  }
  if (current !== 'connection_sent') {
    const n = counts.connection_sent ?? 0;
    if (n > 0) pointers.push(`${n} ${n === 1 ? 'is' : 'are'} waiting on an answer.`);
  }
  if (current !== 'connected') {
    const n = counts.connected ?? 0;
    if (n > 0) pointers.push(`${n} ${n === 1 ? 'is' : 'are'} ready to message.`);
  }

  if (pointers.length === 0) {
    return `${RUNG_EMPTY_TRUTH[current]} Nothing else needs you right now either.`;
  }
  return `${RUNG_EMPTY_TRUTH[current]} ${pointers.join(' ')}`;
}

export default function Outreach(): JSX.Element {
  useDocumentTitle('Outreach — Tacit');

  // A concrete brand, never "all" — see the file comment.
  const [brand, setBrand] = useState<OutreachBrand>('qualiteh');
  const [rung, setRung] = useState<RitualState>('needs_connection');
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const queue = useOutreachQueue(brand, rung, 0);
  const counts = useOutreachCounts(brand);
  const notesSpent = useOutreachNotesSpent();
  const rows = queue.data?.rows ?? [];

  // Whenever the rows actually in view change — a new rung/brand, or a
  // decision on the focused row moved it out of this filter — land on the
  // first row rather than nothing, so the ritual keeps moving forward.
  useEffect(() => {
    if (rows.length === 0) {
      if (focusedId !== null) setFocusedId(null);
      return;
    }
    if (!rows.some((row) => row.id === focusedId)) {
      const first = rows[0];
      if (first) setFocusedId(first.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, focusedId]);

  const focusIndex = rows.findIndex((row) => row.id === focusedId);
  const focusedRow = focusIndex >= 0 ? rows[focusIndex] : null;

  const moveFocus = (delta: number): void => {
    if (rows.length === 0) return;
    const current = focusIndex >= 0 ? focusIndex : 0;
    const next = Math.min(Math.max(current + delta, 0), rows.length - 1);
    const target = rows[next];
    if (target) setFocusedId(target.id);
  };

  const cardRef = useRef<OutreachCardHandle>(null);

  const shortcuts = useMemo<ShortcutConfig[]>(() => [
    { key: 'j', handler: () => moveFocus(1), description: 'Next person' },
    { key: 'k', handler: () => moveFocus(-1), description: 'Previous person' },
    { key: 'Enter', handler: () => cardRef.current?.triggerPrimary(), description: "This rung's primary action" },
    { key: 'o', handler: () => cardRef.current?.openProfile(), description: 'Open profile' },
    { key: 's', handler: () => cardRef.current?.triggerSkip(), description: 'Skip' },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [rows, focusIndex]);

  useKeyboardShortcuts(shortcuts, !isDialogOpen && rows.length > 0);

  return (
    <Layout>
      <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-6">
        <div className="mx-auto max-w-3xl space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <Send className="h-6 w-6 text-primary" aria-hidden="true" />
                <h1 className="text-2xl font-semibold tracking-tight">Outreach</h1>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                Pre-written first-touch messages, sent by hand from your own LinkedIn account.
                Tacit never sends a message itself.
              </p>
            </div>
            <div className="w-52">
              <NativeSelect
                aria-label="Brand"
                value={brand}
                onChange={(event) => setBrand(event.target.value as OutreachBrand)}
              >
                {OUTREACH_BRANDS.map((value) => (
                  <option key={value} value={value}>
                    {OUTREACH_BRAND_LABELS[value]}
                  </option>
                ))}
              </NativeSelect>
            </div>
          </div>

          <div role="tablist" aria-label="Today's outreach plan" className="flex flex-wrap items-stretch gap-2">
            {RITUAL_STATES.map((state) => (
              <button
                key={state}
                type="button"
                role="tab"
                aria-selected={rung === state}
                onClick={() => setRung(state)}
                className={cn(
                  'min-h-11 flex-1 rounded-md border px-4 py-3 text-left transition-colors md:min-h-11',
                  rung === state
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:bg-accent hover:text-accent-foreground',
                )}
              >
                <span className="block text-2xl font-semibold tabular-nums">
                  {counts.isLoading ? '—' : counts.data?.[state] ?? 0}
                </span>
                <span className="block text-xs text-muted-foreground">{RUNG_FILTER_LABEL[state]}</span>
              </button>
            ))}

            <TooltipProvider>
              <div className="flex flex-col justify-center rounded-md border border-dashed border-border px-4 py-3">
                <div className="flex items-center gap-1.5">
                  <span className="text-2xl font-semibold tabular-nums">
                    {notesSpent.isLoading ? '—' : notesSpent.data ?? 0}
                  </span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label="About the notes-spent count"
                        className="flex min-h-11 min-w-11 items-center justify-center text-muted-foreground md:min-h-11 md:min-w-11"
                      >
                        <Info className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      Personalised connection invites are limited on most LinkedIn plans. This
                      counts what you have spent this month — there is no enforced ceiling here,
                      so hold the limit yourself.
                    </TooltipContent>
                  </Tooltip>
                </div>
                <span className="text-xs text-muted-foreground">notes spent this month</span>
              </div>
            </TooltipProvider>
          </div>

          {queue.isLoading ? (
            <div className="space-y-2" role="status" aria-live="polite" aria-busy="true">
              <span className="sr-only">Loading the outreach queue</span>
              <Skeleton className="h-40 w-full" />
            </div>
          ) : queue.isError ? (
            <div className="rounded-lg border border-border p-8 text-center" role="alert" aria-live="assertive">
              <XCircle className="mx-auto h-8 w-8 text-destructive" aria-hidden="true" />
              <p className="mt-3 font-medium">The outreach queue could not be loaded</p>
              <p className="mt-1 text-sm text-muted-foreground">Nothing was changed.</p>
              <Button
                variant="outline"
                className="mt-4 min-h-11 md:min-h-11"
                onClick={() => void queue.refetch()}
              >
                Try again
              </Button>
            </div>
          ) : !focusedRow ? (
            <div className="rounded-lg border border-border p-10 text-center" role="status" aria-live="polite">
              <Inbox className="mx-auto h-9 w-9 text-muted-foreground" aria-hidden="true" />
              <p className="mt-3 font-medium">{emptyRungMessage(rung, counts.data ?? {})}</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  {focusIndex + 1} of {rows.length} · {RUNG_FILTER_LABEL[rung]}
                </span>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="min-h-11 min-w-11 md:min-h-11 md:min-w-11"
                    onClick={() => moveFocus(-1)}
                    disabled={focusIndex <= 0}
                    aria-label="Previous person"
                  >
                    <ChevronUp className="h-4 w-4" aria-hidden="true" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="min-h-11 min-w-11 md:min-h-11 md:min-w-11"
                    onClick={() => moveFocus(1)}
                    disabled={focusIndex >= rows.length - 1}
                    aria-label="Next person"
                  >
                    <ChevronDown className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              </div>

              <OutreachCard
                key={focusedRow.id}
                ref={cardRef}
                row={focusedRow}
                onClaimConflict={() => void queue.refetch()}
                onDialogOpenChange={setIsDialogOpen}
              />

              <p className="text-xs text-muted-foreground/70">{RUNG_SHORTCUT_HINT[rung]}</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
